// traceShellWork - the shell door into every X-ray domain, read with a grammar.
//
// Each domain extractor deliberately declines the shell. traceWebResearch says
// so outright ("a page fetched through Bash `curl` is out of scope here,
// exactly like shell file edits are out of scope for the Files tree"), and the
// exceptions were bolted on one at a time and only for WRITES:
// traceShellOrigins recovers redirect destinations, traceDerivedFileOps derives
// content from heredocs and `sed -i`, traceForgeOps reads `gh`. Nothing reads
// the shell for what an agent CONSULTED.
//
// The cost is a hole in the middle of the timeline. A `cat src/x.js` lands in a
// lane whose identity is the string "Bash" - the tool's name, not the subject -
// so the file's own lane never learns it was read, and the reconstruction the
// belief store serves never sees bytes the agent demonstrably had in front of
// it. Writes are covered and reads are not, which means the timeline knows what
// changed and not what was consulted before the change.
//
// This is one parse per shell call, dispatched through a table keyed by program.
// One parse rather than each domain re-deriving its own is the point: five
// modules currently reach into shell text with five sets of regexes.
//
// WHAT MAKES A READ WORTH RENDERING IS ITS POSITION. The file X-ray's read op
// carries `obs` - (line number, text) pairs - which is what lets a
// reconstruction place observed content in the file rather than pile it up.
// A grammar can recover those numbers, because the command states them:
//
//   cat f                 the whole file, from line 1
//   head -n K f           lines 1..K
//   sed -n '10,40p' f     lines 10..40, stated exactly
//   tail -n K f           the LAST K lines, whose absolute numbers are unknown
//   grep -n p f           the result carries its own numbers
//   grep p f              matched lines, positions unknown
//
// The ones whose positions are unknown still emit a read - the file WAS
// consulted, and that belongs on its lane - but with no `obs`, because a
// reconstruction that placed tail's output at line 1 would state something
// false. That distinction is the whole reason to parse rather than pattern
// match.
//
// PIPES BREAK ATTRIBUTION. A tool result is the output of the WHOLE command, so
// `cat a | grep b` returns grep's output, not the file. A reader inside a
// pipeline emits a read with no content: the file was consulted, and what came
// back describes something else.
import { buildSemanticFeed } from "./traceSemanticFeed.js";
import { interpreterPayload, powershellLiteral, readShellCommands, SHELL_LANGUAGES } from "./syntax/shellAst.js";
import { shellToolCommand } from "./shellToolCall.js";
import { readCapability } from "./syntax/capability.js";
import { detectShellLanguage } from "./traceCapability.js";
import { actionShellCalls, buildTraceActions } from "./traceActions.js";

// Same ceiling the capability pass uses: one parse per shell call, and a trace
// is a whole conversation.
export { MAX_SHELL_CALLS } from "./traceCapability.js";
// A read observation is bounded the way the file X-ray's is; a 40MB `cat` is
// not a reading, it is a denial of service against the reconstruction.
const MAX_OBS_LINES = 2_000;
const MAX_LINE_CHARS = 2_000;
// How often the read pass yields to the event loop (paintability) versus how
// many partials it may publish over a whole pass (each costs the consumer a
// full lane-model rebuild). The strides are set where readShellWork uses them.
const YIELD_STRIDE = 25;
const MAX_PUBLISHES = 8;
// A command naming a hundred paths is a sweep, not a set of reads.
const MAX_PATHS_PER_COMMAND = 16;

// A directory is not a file lane. `grep -rn TODO src/components` consulted a
// tree, and minting `files:src/components` would claim a file that does not
// exist. Those land in the census instead, where their mass is visible.
// Extensionless names agents read constantly. The extension rule exists to
// keep directories and search patterns from minting file lanes, but alone it
// censused every `cat Makefile` and `cat /etc/hosts`.
const KNOWN_EXTENSIONLESS = new Set([
  "makefile", "dockerfile", "readme", "license", "licence", "changelog",
  "contributing", "authors", "notice", "codeowners", "gemfile", "rakefile",
  "justfile", "procfile", "vagrantfile", "brewfile", "hosts",
]);
const looksLikeFile = (v) => {
  const t = String(v).replace(/[\\/]+$/, "");
  // A glob names a SWEEP - `*/migrations/*.sql` in a git pathspec minted a
  // file lane whose "file" was a pattern. One file has no metacharacters.
  if (/[*?[]/.test(t)) return false;
  if (/\.[A-Za-z0-9_]{1,12}$/.test(t)) return true;
  const base = t.slice(Math.max(t.lastIndexOf("/"), t.lastIndexOf("\\")) + 1).toLowerCase();
  return KNOWN_EXTENSIONLESS.has(base);
};
// A bare `x.js` is a path - it is the most common way an agent names a file in
// the working directory, and requiring a separator dropped every one of them.
const looksLikePath = (v) => /[\\/]/.test(v) || /^[.~]/.test(v) || /^[a-z]:[\\/]/i.test(v) || looksLikeFile(v);
const looksLikeUrl = (v) => /^https?:\/\//i.test(v);

/**
 * The arguments that can name a subject: positionals, plus the values of flags
 * that TAKE a subject.
 *
 * The two kinds of value-consuming flag are opposite and were conflated at
 * first: `head -n 20` consumes a count that must be skipped, while
 * `Get-Content -Path x.ps1` consumes the very path being read. Skipping past
 * both dropped every PowerShell read.
 */
function subjectArgs(command, { valueFlags = null, pathFlags = null } = {}) {
  const out = [];
  for (let i = 0; i < command.args.length; i += 1) {
    const a = command.args[i];
    if (a.kind !== "flag") {
      out.push(a);
      continue;
    }
    if (pathFlags?.test(a.text)) {
      const value = command.args[i + 1];
      if (value && value.kind !== "flag") out.push(value);
      i += 1;
      continue;
    }
    if (valueFlags?.test(a.text)) i += 1;
  }
  return out;
}

// -- readers ----------------------------------------------------------------
// Each returns the first line number the output corresponds to, or null when
// the command does not state one. Null is not a failure: it is the difference
// between content that can be placed and content that can only be reported.

// ONLY the bare `-n`, because only the bare form takes the next argument. The
// count can also ride the flag - `head -n5 f`, `head -20 f`, `head -n=5 f` -
// and treating those as value-consuming ate the path that followed, silently
// dropping the read. A self-contained count is just a flag: skipped, consuming
// nothing.
const HEAD_COUNT = /^-n$/;

function headStart() {
  return 1;
}

function sedRange(command) {
  // `sed -n '10,40p'` and `sed -n '12p'` state their own range. Anything else
  // (a substitution, an address regex) is a transform, not a positioned read.
  if (!command.args.some((a) => /^-[a-z]*n[a-z]*$/.test(a.text))) return null;
  for (const a of subjectArgs(command)) {
    const m = /^'?(\d+)(?:,(\d+))?p'?$/.exec(a.value);
    if (m) return Number(m[1]);
  }
  return null;
}

// cat numbers what it prints under -n/--number and -b/--number-nonblank,
// possibly ganged with other short flags (`cat -bn`). Numbered output is a
// RENDERING, read through the gutter parser rather than as content.
const CAT_NUMBER_FLAGS = /^-(?:[A-Za-z]*[nb][A-Za-z]*|-number(?:-nonblank)?)$/;

// Get-Content's bounds ride flags. `-TotalCount K` (aliases -Head, -First)
// stops after K lines: positioned from 1, but a prefix, never the whole file.
// `-Tail N` (alias -Last) is tail: the LAST N lines, absolute numbers
// unknowable. `gc` is the same cmdlet and must consume the same flags, or
// `gc x -Tail 5` falls through to a whole-file claim.
const GET_CONTENT = {
  start: (c) => (c.args.some((a) => /^-(?:Tail|Last)$/i.test(a.text)) ? null : 1),
  whole: (c) => !c.args.some((a) => /^-(?:TotalCount|Head|First|Tail|Last)$/i.test(a.text)),
  pathFlags: /^-(?:Path|LiteralPath)$/i,
  valueFlags: /^-(?:Encoding|TotalCount|Head|First|Tail|Last)$/i,
};

// program -> how the output is positioned, and which args can be paths.
// `whole` may depend on the command's own flags, so it can be a predicate.
const READERS = {
  cat: { start: () => 1, whole: true, guttered: (c) => c.args.some((a) => CAT_NUMBER_FLAGS.test(a.text)) },
  nl: { start: () => 1, guttered: () => true },
  "get-content": GET_CONTENT,
  gc: GET_CONTENT,
  type: { start: () => 1, whole: true },
  head: { start: headStart, valueFlags: HEAD_COUNT },
  // The last K lines: real content at unknowable absolute positions.
  tail: { start: () => null, valueFlags: HEAD_COUNT },
  // sed's first positional is its SCRIPT unless one of `scriptFlags` carried
  // it: `sed 's/foo/bar/' f` consulted f alone, and the substitution text is
  // not a path this call read.
  sed: { start: sedRange, valueFlags: /^-(?:e|f|-expression|-file)$/, scriptFlags: /^-(?:e|f|-expression(?:=|$)|-file(?:=|$))/ },
  less: { start: () => null },
  more: { start: () => null },
};

// Searchers name files too, and a match is a consultation of that file. `-n`
// makes the result self-positioning; without it the lines are real but placed
// nowhere.
// `patternFlags` marks the spellings that carry the pattern in a FLAG
// (`grep -e TODO`, `Select-String -Pattern TODO`): the positional-pattern
// slice below must not run for those, or it eats the first real path - the
// only one, in the common single-file case.
const AWK_SEARCHER = {
  // awk's output is a TRANSFORM of what it read, so no spelling of it is
  // self-positioning: files place, content never does. The first positional
  // is its program unless -f carried one from a file, which is the same
  // pattern-carried rule grep's -e already uses.
  numbered: /^$/,
  valueFlags: /^-(?:v|F|f)$/,
  patternFlags: /^-[A-Za-z]*f$|^--file(?:=|$)/,
};

const SEARCHERS = {
  grep: { numbered: /^-[a-z]*n[a-z]*$/, valueFlags: /^-(?:e|f|m|A|B|C|-include|-exclude)$/, patternFlags: /^-(?:e|f)$|^--(?:regexp|file)(?:=|$)/ },
  awk: AWK_SEARCHER,
  gawk: AWK_SEARCHER,
  mawk: AWK_SEARCHER,
  nawk: AWK_SEARCHER,
  rg: { numbered: /^(?:-[a-z]*n[a-z]*|--line-number)$/, valueFlags: /^-(?:e|f|m|A|B|C|-glob|-type)$/, patternFlags: /^-(?:e|f)$|^--(?:regexp|file)(?:=|$)/ },
  ag: { numbered: /^-[a-z]*n[a-z]*$/ },
  "select-string": { numbered: /^$/, pathFlags: /^-(?:Path|LiteralPath)$/i, valueFlags: /^-(?:Pattern|Context)$/i, patternFlags: /^-Pattern$/i },
};

const FETCHERS = new Set(["curl", "wget", "invoke-webrequest", "invoke-restmethod"]);

// -- version control --------------------------------------------------------
// Git is the largest single body of work the shell door was hiding: 31% of
// real commands. It splits two ways, and the split is what makes it worth
// reading rather than counting.
//
// FILE-SCOPED git is a file read. `git show <rev>:<path>` prints that file at
// that revision - a whole file, from line 1, which is a POSITIONED read of the
// same kind `cat` produces. Measured over the corpus, `git show` names a
// file-shaped path in 731 of 882 invocations, so this is the common case and
// not an edge one. A pathspec after `--` (`git log -- src/x.js`) names the file
// too, but its output is history rather than contents, so that is a
// consultation without content.
//
// REPO-SCOPED git is its own subject. `git status`, `git log`, `git commit`
// act on the working tree, and the lane that carries them is the tree itself.
const GIT_READ_VERBS = new Set([
  "log", "show", "diff", "status", "blame", "grep", "branch", "tag", "describe", "reflog",
  "rev-parse", "rev-list", "merge-base", "ls-tree", "ls-files", "ls-remote", "cat-file",
  "for-each-ref", "check-ignore", "shortlog", "whatchanged", "count-objects", "range-diff",
]);
const GIT_WRITE_VERBS = new Set([
  "add", "commit", "push", "merge", "rebase", "checkout", "switch", "restore", "reset",
  "stash", "mv", "rm", "revert", "cherry-pick", "apply", "clean", "fetch", "pull", "clone",
  "worktree", "init", "submodule",
]);
// Verbs that MUTATE the working tree at the paths they were scoped to:
// `git checkout -- f` overwrites f, `git rm -- f` deletes it. `add` stages
// content and `log` consults history; neither touches the file itself.
const GIT_PATHSPEC_WRITE_VERBS = new Set(["checkout", "restore", "rm"]);
// `git -C <dir>` and `git -c <cfg>` sit before the verb and take a value.
// Without peeling them the verb reads as a directory path, which is how the
// corpus census reported filesystem paths as git subcommands.
const GIT_PREFIX_FLAGS = /^-(?:C|c|-git-dir|-work-tree|-namespace|-exec-path)$/;
// `HEAD:src/x.js`, `abc123:path/to/f.js` - a revision and a path in one token.
const GIT_REV_PATH = /^([^\s:]+):([^\s:]{2,})$/;

// -- builds -----------------------------------------------------------------
// A build tool's lane is the tool, and its ops are its verbs. What makes it
// worth a lane is the OUTCOME: a session's failing tests are legible as red
// marks in one row rather than scattered through unclassified shell calls.
const BUILD_TOOLS = {
  cargo: { skip: /^\+/ },
  npm: {},
  npx: {},
  pnpm: {},
  yarn: {},
  uv: {},
  bun: {},
  make: {},
  gradle: {},
  mvn: {},
  pytest: {},
  tox: {},
  // `node script.mjs` runs a script; the script IS the verb.
  node: {},
  deno: {},
};

// Plumbing, not work with a subject. The census exists to rank the work this
// module cannot yet see, and it is useless if the top of it is `echo` and `cd`:
// those have no subject to miss. A reader or searcher that found no path is
// the same case - `... | head -20` filters a stream, it does not read a file -
// and is excluded at its own branch rather than listed here.
const PLUMBING = new Set([
  "echo", "printf", "cd", "pushd", "popd", "dirs", "sleep", "true", "false", "test", "seq",
  "export", "set", "unset", "source", "break", "continue", "return", "exit", "eval", "shift",
  "date", "basename", "dirname", "realpath", "readlink", "pwd", "whoami", "hostname", "uname",
  "wc", "cut", "sort", "uniq", "tr", "column", "fold", "rev", "tac", "paste", "join", "xargs",
  "tee", "yes", "which", "type", "command", "time", "watch",
  "write-host", "write-output", "out-string", "measure-object", "select-object", "sort-object",
]);

/**
 * Split a reader's result into positioned observation lines.
 *
 * Returns null when the output cannot be placed - which is a real answer, not
 * an empty one: the caller emits a read without content rather than a read
 * whose content claims a position it never had. `capped` says the output had
 * more lines than the bound admitted, which makes whatever was kept a prefix.
 */
function observationFrom(text, startLine) {
  if (startLine == null || !text) return null;
  const lines = String(text).split("\n");
  // A trailing newline is a line terminator, not an empty last line.
  if (lines.length && lines[lines.length - 1] === "") lines.pop();
  if (!lines.length) return null;
  const obs = [];
  for (let i = 0; i < lines.length && obs.length < MAX_OBS_LINES; i += 1) {
    obs.push([startLine + i, lines[i].slice(0, MAX_LINE_CHARS)]);
  }
  return obs.length ? { obs, capped: lines.length > obs.length } : null;
}

/** `grep -n` output positions itself: `12:matched text`. */
function numberedObservation(text) {
  if (!text) return null;
  const obs = [];
  for (const line of String(text).split("\n")) {
    const m = /^(?:[^:\n]*:)??(\d+):(.*)$/.exec(line);
    if (!m) continue;
    obs.push([Number(m[1]), m[2].slice(0, MAX_LINE_CHARS)]);
    if (obs.length >= MAX_OBS_LINES) break;
  }
  return obs.length ? obs : null;
}

// `cat -n` and `nl` return a RENDERING: a number gutter ahead of every line.
// Recording the gutter as content would state text that is not in the file,
// so the gutter is stripped and its numbers - which ARE the true positions -
// take the place of a stated start. Both tools right-align in a 6-wide field
// before a tab; any line that does not carry that shape (nl leaves blank
// lines unnumbered) refuses the WHOLE observation, because a partial strip
// would misalign every line after it.
function gutteredObservation(text) {
  if (!text) return null;
  const lines = String(text).split("\n");
  if (lines.length && lines[lines.length - 1] === "") lines.pop();
  if (!lines.length) return null;
  const obs = [];
  for (const line of lines) {
    if (obs.length >= MAX_OBS_LINES) break;
    const m = /^ {0,5}(\d+)\t(.*)$/.exec(line);
    if (!m) return null;
    obs.push([Number(m[1]), m[2].slice(0, MAX_LINE_CHARS)]);
  }
  return obs;
}

const GUTTER_LINE = /^ {0,5}(\d+)\t/;

/**
 * Whether a numbering reader's gutter is the FILE's line numbering. `cat -n`
 * and `nl -ba` number every line. Bare `nl` numbers only non-blank lines: the
 * gutter parse catches that on a whole file (an unnumbered line refuses the
 * reading), but not through a filter, where the blank lines it skipped are
 * out of sight and every number after them is short. Any other flag (a start
 * value, an increment, a separator, a width) moves the gutter off the file's
 * numbering too, so only these spellings qualify.
 */
function numbersEveryLine(command) {
  const flags = command.args.filter((a) => a.kind === "flag");
  if (command.program === "cat") return flags.length > 0 && flags.every((a) => /^-n$|^--number$/.test(a.text));
  if (command.program !== "nl") return false;
  let all = false;
  for (let i = 0; i < command.args.length; i += 1) {
    const a = command.args[i];
    if (a.kind !== "flag") continue;
    if (/^-ba$|^--body-numbering=a$/.test(a.text)) { all = true; continue; }
    if (a.text === "-b" && command.args[i + 1]?.value === "a") { all = true; i += 1; continue; }
    return false;
  }
  return all;
}

/**
 * One file's lines out of a search over several: numbered, each match reads
 * `path:N:text` and each context line `path-N-text`, so every line names its
 * own file and position whatever else shares the stream. Lines are matched on
 * the path exactly as the command spelled it, which is how the tool prints it.
 */
function prefixedObservation(text, path) {
  if (!text || !path) return null;
  const obs = [];
  for (const line of String(text).split("\n")) {
    if (!line.startsWith(path)) continue;
    const m = /^[:-](\d+)[:-](.*)$/.exec(line.slice(path.length));
    if (!m) continue;
    obs.push([Number(m[1]), m[2].slice(0, MAX_LINE_CHARS)]);
    if (obs.length >= MAX_OBS_LINES) break;
  }
  return obs.length ? obs : null;
}

// The path a line of Select-String output names, against the path the command
// spelled. It prints a file under the working directory relative to it and
// any other file in full, so `..\x.yml` comes back as `C:\...\x.yml`: the
// printed path matches when it is the spelling, or when the spelling climbs
// out of the directory and the printed path is a full one ending with what is
// left of it once the climb is taken off. A relative printed path is never a
// climbed-out file's, however its name ends.
function samePrintedPath(printed, spelled) {
  const norm = (p) => String(p).replace(/\\/g, "/").replace(/^(?:\.\/)+/, "");
  const a = norm(printed);
  const b = norm(spelled);
  if (a === b) return true;
  const tail = b.replace(/^(?:\.\.\/)+/, "");
  return tail !== b && tail.length > 0 && /^(?:[A-Za-z]:)?\//.test(a) && a.endsWith(`/${tail}`);
}

/**
 * Select-String's own numbering: `path:N:text` per match, `> ` or two spaces
 * in front under -Context, blank lines the formatter's own. The host wraps a
 * long line at its width - counting the match highlighting it then strips, so
 * where the cut fell is not recoverable - and the tail lands on lines of its
 * own. A line that is not a record therefore means the record before it was
 * cut, and that record's text is not whole: it is dropped and every whole one
 * kept. `others` are the other files the same search named, whose records end
 * this file's; a record two of its spellings could both name is neither's.
 * Output before any record is not Select-String's at all.
 */
function selectStringObservation(text, spelled, others = []) {
  const records = [];
  let last = null;
  let seen = false;
  for (const line of String(text || "").split("\n")) {
    if (!line.trim()) continue;
    const m = /^(?:> | {2})?(.+?):(\d+):(.*)$/.exec(line);
    const mine = Boolean(m) && samePrintedPath(m[1], spelled);
    const theirs = Boolean(m) && others.some((o) => samePrintedPath(m[1], o));
    if (mine || theirs) {
      seen = true;
      last = mine && !theirs ? { line: Number(m[2]), text: m[3], cut: false } : null;
      if (last) records.push(last);
      continue;
    }
    if (!seen) return null;
    if (last) last.cut = true;
  }
  const obs = records.filter((r) => !r.cut).slice(0, MAX_OBS_LINES).map((r) => [r.line, r.text.slice(0, MAX_LINE_CHARS)]);
  return obs.length ? obs : null;
}

/**
 * Select-String's records at the tail of a stream other statements share.
 * It writes a blank line before its first match, so when it printed last,
 * its records are the lines after the last blank one: whatever another
 * statement printed - a linter's own `path:N:` lines among it - comes before
 * that line. No blank line, no way to tell where its records begin.
 */
function trailingSelectStringRecords(text, spelled, others) {
  const lines = String(text || "").split("\n");
  let end = lines.length;
  while (end > 0 && !lines[end - 1].trim()) end -= 1;
  let start = end;
  while (start > 0 && lines[start - 1].trim()) start -= 1;
  if (start === 0 || start === end) return null;
  return selectStringObservation(lines.slice(start, end).join("\n"), spelled, others);
}

/**
 * ForEach-Object as the template a search's matches print through:
 * `{ "$($_.LineNumber): $($_.Line)" }`, or `{ "{0}: {1}" -f $_.LineNumber,
 * $_.Line }`. The number comes first, then a separator that cannot run into
 * it, then the line; strings print unwrapped. The line may come trimmed
 * (`$_.Line.Trim()`, `.TrimStart()`, `.TrimEnd()`): the file's line less its
 * surrounding whitespace, which `trimmed` says so the reconstruction never
 * takes it for the line itself. A template that prints anything else states
 * no line of the file. `{ pattern, trimmed }`, or null.
 */
function selectStringTemplate(command) {
  const blocks = command.args.filter((a) => a.kind !== "flag");
  if (blocks.length !== 1 || command.args.some((a) => a.kind === "flag" && !/^-Process$/i.test(a.text))) return null;
  const body = /^\{\s*([\s\S]*?)\s*\}$/.exec(blocks[0].text)?.[1];
  if (body == null) return null;
  const line = String.raw`\$_\.(?:LineNumber|Line(?:\.Trim(?:Start|End)?\(\))?)`;
  let parts = null;
  const expanding = new RegExp(String.raw`^"((?:[^"\x60$]|\$\(\s*${line}\s*\))*)"$`, "i").exec(body);
  if (expanding) {
    const split = expanding[1].split(new RegExp(String.raw`\$\(\s*\$_\.(LineNumber|Line(?:\.Trim(?:Start|End)?\(\))?)\s*\)`, "i"));
    if (split.length === 5 && /^linenumber$/i.test(split[1]) && /^line(?:\.trim(?:start|end)?\(\))?$/i.test(split[3])) parts = [split[0], split[2], split[4], split[3]];
  } else {
    const format = /^"([^"`${}]*)\{0\}([^"`${}]*)\{1\}([^"`${}]*)"\s+-f\s+\$_\.LineNumber\s*,\s*\$_\.(Line(?:\.Trim(?:Start|End)?\(\))?)$/i.exec(body);
    if (format) parts = [format[1], format[2], format[3], format[4]];
  }
  if (!parts || !parts[1] || /^\d/.test(parts[1])) return null;
  const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return {
    pattern: new RegExp(`^${esc(parts[0])}(\\d+)${esc(parts[1])}(.*)${esc(parts[2])}$`),
    trimmed: !/^line$/i.test(parts[3]),
  };
}

// The lines a search printed through its template, numbered by the search:
// every line of the span is one, in file order, or the span is not the
// pipeline's alone.
function templateObservation(text, template) {
  const obs = [];
  for (const line of String(text || "").split("\n")) {
    if (!line) continue;
    const m = template.pattern.exec(line);
    if (!m || (obs.length && Number(m[1]) <= obs[obs.length - 1][0])) return null;
    if (obs.length < MAX_OBS_LINES) obs.push([Number(m[1]), m[2].slice(0, MAX_LINE_CHARS)]);
  }
  return obs.length ? obs : null;
}

/**
 * The gate positioned observations pass through.
 *
 * The module's invariant - never state positioned content that did not run -
 * was first enforced piecemeal at each emission site, and each site forgot a
 * different guard. Attribution requires all three at once: a BARE command
 * (the tool result IS its output), a call that did not fail (what came back
 * is an error message, not the file), and exactly ONE named target (two
 * files concatenated into one stream cannot be split back apart). `obsFull`
 * asks for more still: an unbounded read from line 1 whose output survived
 * the cap, because a capped observation is a prefix of the file, not the
 * file. Self-positioning output (`grep -n`, a number gutter) is trusted for
 * position, never for coverage, so it can never claim the whole file.
 * Output that names its own file on every line - `grep -n` over several
 * files, Select-String reading files - is the one exception to the
 * single-target rule and parses on its own (prefixedObservation,
 * selectStringObservation): still only from a call that did not fail, and
 * never as the whole file.
 */
function observe({ resultText, bare, failed, targets, start = null, whole = false, numbered = false, guttered = false }) {
  if (!bare || failed || targets !== 1) return { obs: [], readLines: 0, obsFull: false };
  if (numbered || guttered) {
    const obs = (numbered ? numberedObservation(resultText) : gutteredObservation(resultText)) || [];
    return { obs, readLines: obs.length, obsFull: false };
  }
  const read = observationFrom(resultText, start);
  if (!read) return { obs: [], readLines: 0, obsFull: false };
  return { obs: read.obs, readLines: read.obs.length, obsFull: Boolean(whole && start === 1 && !read.capped) };
}

// -- same-line cwd ------------------------------------------------------------
// `cd dashboard && cat src/x.js` read dashboard/src/x.js, and an unprefixed
// emission merges it onto the lane of a root-level src/x.js - a different
// file. So `cd` (and `pushd`) is folded LEFT TO RIGHT across the commands of
// one parsed line, prefixing the relative subject paths after it. The fold is
// bounded to ONE line BY DESIGN: cwd is process state that persists across
// calls, but carrying it forward would let a single mis-read `cd` silently
// re-root every lane that follows it, so a subject is only re-rooted by a
// `cd` visible in the same source the subject came from.

const PATH_ROOT = /^(?:[\\/]|[a-z]:[\\/]|~[\\/])/i;
const isRooted = (v) => /^[\\/~]/.test(v) || /^[a-z]:[\\/]/i.test(v);

// Join a folded cwd onto a relative subject, resolving the dots that resolve
// statically. A `..` that would climb above what the fold knows has no
// lane-joinable spelling, so the join refuses rather than guesses.
function joinSubject(cwd, value) {
  const root = PATH_ROOT.exec(cwd)?.[0].replace(/\\/g, "/") ?? "";
  const parts = cwd.slice(root.length).split(/[\\/]+/).filter((s) => s && s !== ".");
  for (const seg of String(value).split(/[\\/]+/)) {
    if (!seg || seg === ".") continue;
    if (seg === "..") {
      if (!parts.length) return null;
      parts.pop();
      continue;
    }
    parts.push(seg);
  }
  return parts.length ? root + parts.join("/") : null;
}

/** The cwd after a `cd`/`pushd`: a new prefix, or null once unknowable. */
function foldCwd(cwd, command) {
  const dir = command.args.find((a) => a.kind !== "flag");
  // Bare `cd` goes to $HOME and `cd -` to a directory this fold never saw.
  if (!dir || !dir.resolved || dir.value === "-") return null;
  const value = dir.value.replace(/(.)[\\/]+$/, "$1");
  if (isRooted(value)) return value;
  return cwd == null ? null : joinSubject(cwd, value);
}


// -- segmented attribution ----------------------------------------------------
// The single most common multi-read idiom refuses under the bare gate while
// being fully self-describing:
//
//   echo "=== models ===" && git show REV:src/models.rs | sed -n '570,585p'
//   echo "=== proto ===" && git show REV:proto/api.proto | sed -n '1004,1022p'
//
// The command states the files, the exact line ranges, and - through the
// echoed markers - how to split the one result stream back into per-command
// segments. Nothing here is guessed: a marker must appear verbatim as its own
// output line in order, a filter chain contributes positions only when every
// stage is a recognised contiguous range (`sed -n 'A,Bp'`, `head -n K`), and a
// segment longer than its stated range refuses. Any structural surprise - a
// missing marker, an unknown filter, a redirect, a background `&`, an
// interpreter payload sharing the stream - drops back to today's answer: the
// read is placed, the content is not.
//
// Several producers between two markers (or with no markers at all) split by
// ARITHMETIC instead. Code-mode agents batch their reads without markers:
//
//   sed -n '56,125p' a.yaml
//   sed -n '1,230p' b.yaml
//   sed -n '1,220p' docs/recovery.md
//
// Each command bounds its own output (70, 230 and 220 lines), and no command
// can print more than its bound. So when the span holds exactly the sum of the
// bounds, every command printed its full bound, and the boundaries fall where
// the bounds put them. A span of any other length refuses, since a file that
// ended early could be any of them.

const PIPE_CONNECTOR = /^\s*\|\s*$/;
// `&&`, `;` and newlines order their outputs; `||` runs at most one side and
// a lone `&` interleaves, so both refuse.
const STEP_CONNECTOR = /^(?:\s|;|&&|\n)+$/;
const SILENT_PROGRAMS = new Set(["cd", "pushd", "popd", "export", "set", "true", ":"]);
// PowerShell's own: a single-argument Write-Output prints its text, a location
// change prints nothing. (Write-Output with several arguments prints one line
// per argument, so only one argument makes a marker.)
const PS_MARKER_PROGRAMS = new Set(["write-output", "write-host", "echo"]);
const PS_SILENT_PROGRAMS = new Set(["set-location", "sl", "cd", "chdir", "push-location", "pushd", "pop-location", "popd"]);
const PS_FOREACH_PROGRAMS = new Set(["foreach-object", "%", "foreach"]);
// What moves the working directory, in both shells (`Set-Location` and its
// aliases are PowerShell's `cd`).
const CWD_PROGRAMS = new Set(["cd", "pushd", "set-location", "sl", "chdir", "push-location"]);

// sed as a pure range FILTER: `-n`, one positional script naming one print
// range, no script files, no input files (stdin is the pipe).
function sedFilterRange(command) {
  if (!command.args.some((a) => /^-[a-z]*n[a-z]*$/.test(a.text))) return null;
  if (command.args.some((a) => a.kind === "flag" && /^-[a-z]*[ef]/.test(a.text))) return null;
  const values = command.args.filter((a) => a.kind === "value");
  if (values.length !== 1 || !values[0].resolved) return null;
  const m = /^(\d+)(?:,(\d+))?p$/.exec(values[0].value);
  if (!m) return null;
  const from = Number(m[1]);
  const to = Number(m[2] ?? m[1]);
  return to >= from ? { from, to } : null;
}

// head as a filter: the line count however it is spelled, 10 when bare, null
// when any other flag makes the output something besides a line prefix.
function headFilterCount(command) {
  let count = null;
  for (let i = 0; i < command.args.length; i += 1) {
    const a = command.args[i];
    if (a.kind === "value") return null;
    if (HEAD_COUNT.test(a.text)) {
      const value = command.args[i + 1];
      if (!value?.resolved || !/^\d+$/.test(value.value)) return null;
      count = Number(value.value);
      i += 1;
      continue;
    }
    const glued = /^-n?(\d+)$/.exec(a.text);
    if (!glued) return null;
    count = Number(glued[1]);
  }
  return count ?? 10;
}

/**
 * The most lines a producer can print, when its own text bounds it: a stated
 * range (`sed -n 'A,Bp' f`) or a count (`head -n K f`, `tail -n K f`).
 * Infinity when nothing does. head and tail put a `==> f <==` banner before
 * each of several files, so they bound only a single literal file. sed reads
 * several files as one stream, so its range bounds them together.
 */
function producerCap(command) {
  if (command.program === "sed") {
    let quiet = false;
    let script = null;
    for (const a of command.args) {
      if (a.kind === "flag") {
        // -E/-r change only the regex dialect. Anything else - -i writes the
        // file instead, -s restarts the range per file, -e/-f move the script -
        // leaves the output unbounded as far as this reading can tell.
        if (!/^-[nEr]+$|^--(?:quiet|silent|regexp-extended)$/.test(a.text)) return Infinity;
        if (/^-[Er]*n[nEr]*$|^--(?:quiet|silent)$/.test(a.text)) quiet = true;
        continue;
      }
      script ??= a;
    }
    const m = quiet && script?.resolved ? /^(\d+)(?:,(\d+))?p$/.exec(script.value) : null;
    if (!m) return Infinity;
    const from = Number(m[1]);
    const to = Number(m[2] ?? m[1]);
    return to >= from ? to - from + 1 : Infinity;
  }
  if (command.program === "head" || command.program === "tail") {
    let count = 10;
    const files = [];
    for (let i = 0; i < command.args.length; i += 1) {
      const a = command.args[i];
      if (a.kind === "value") {
        files.push(a);
        continue;
      }
      if (HEAD_COUNT.test(a.text)) {
        const value = command.args[i + 1];
        if (!value?.resolved || !/^\d+$/.test(value.value)) return Infinity;
        count = Number(value.value);
        i += 1;
        continue;
      }
      const glued = /^-n?(\d+)$/.exec(a.text) || /^--lines=(\d+)$/.exec(a.text);
      if (!glued) return Infinity;
      count = Number(glued[1]);
    }
    return files.length === 1 && files[0].resolved ? count : Infinity;
  }
  if (READERS[command.program] === GET_CONTENT) {
    // Get-Content's count rides a flag (-TotalCount, -Head, -First, -Tail,
    // -Last), and bounds one file: several print one after another.
    if (subjectArgs(command, GET_CONTENT).length !== 1) return Infinity;
    for (let i = 0; i < command.args.length; i += 1) {
      if (!/^-(?:TotalCount|Head|First|Tail|Last)$/i.test(command.args[i].text)) continue;
      const value = command.args[i + 1];
      return value?.resolved && /^\d+$/.test(value.value) ? Number(value.value) : Infinity;
    }
  }
  return Infinity;
}

// What a reader prints when it fails. stderr shares the stream, and a short
// error standing in for a short read is the one way a span's length can equal
// the sum of its bounds while holding the wrong lines - so any such line
// refuses the arithmetic split.
const READ_ERROR_LINE = new RegExp([
  ": (?:No such file or directory|Permission denied|Is a directory)$",
  "^(?:g?sed|head|tail|cat|nl): (?:can't read|cannot open|couldn't open|read error|error reading|invalid|-e expression|\\d+: )",
  "^fatal: ",
  // PowerShell's: a cmdlet's name before the message (`Get-Content: ...`,
  // `Get-Content : ...` in Windows PowerShell), the message itself, the error
  // view's `Line |` gutter and `~~~` underline, 5.1's `+ CategoryInfo`
  // trailer, and the colour codes 7's view wraps it in. Each is narrower than
  // it looks necessary to be: a markdown table row starts with `| ` and an
  // HTTP header with `Word-Word: `, and a file holding either must still read.
  "^(?:Get|Set|New|Remove|Select|Invoke|Test|Out|Write|Import|Export|Copy|Move|Add|Clear|Resolve|Join|Split|Push|Pop|Measure|Sort|Where|ForEach)-[A-Z][A-Za-z]+ ?: ",
  "Cannot find path .* because it does not exist",
  "^\\s*Line \\|\\s*$",
  "^\\s+\\d+ \\| ",
  "^\\s+\\| [ ~]*~",
  "^\\s*\\+ (?:CategoryInfo|FullyQualifiedErrorId)",
  "\\u001b\\[",
].join("|"));

const GREP_FILTER_FLAGS = /^-[inEFwvh]+$/;

// Select-Object as a line filter: -First, -Skip and -Index (zero-based, a run
// like `(0..40)`) take a contiguous run of the lines it is fed, -Last its
// tail. Any other parameter selects or reshapes properties, which is no line
// window at all. Null when the filter is not one of those.
function selectObjectWindow(command) {
  let skip = 0;
  let take = null;
  let last = null;
  let index = null;
  for (let i = 0; i < command.args.length; i += 1) {
    const a = command.args[i];
    const value = command.args[i + 1];
    if (a.kind !== "flag" || !value || value.kind === "flag" || !value.resolved) return null;
    i += 1;
    const flag = a.text.toLowerCase();
    if (flag === "-index") {
      const m = /^\(?\s*(\d+)\s*(?:\.\.\s*(\d+))?\s*\)?$/.exec(value.value);
      if (!m || Number(m[2] ?? m[1]) < Number(m[1])) return null;
      index = { from: Number(m[1]), to: Number(m[2] ?? m[1]) };
      continue;
    }
    if (!/^\d+$/.test(value.value)) return null;
    if (flag === "-first") take = Number(value.value);
    else if (flag === "-skip") skip = Number(value.value);
    else if (flag === "-last") last = Number(value.value);
    else return null;
  }
  if (index) return take == null && last == null && !skip ? { skip: index.from, take: index.to - index.from + 1, last: null } : null;
  if (last != null) return take == null && !skip ? { skip: 0, take: null, last } : null;
  return { skip, take, last: null };
}

// Select-String fed lines by a pipe prints the lines that match, unnumbered:
// a subset of the stream, at positions it does not state. -Context adds
// marked neighbours and -Quiet prints a boolean, so neither is that subset.
function selectStringFilters(command) {
  for (let i = 0; i < command.args.length; i += 1) {
    const a = command.args[i];
    if (a.kind !== "flag") continue;
    const flag = a.text.toLowerCase();
    if (flag === "-pattern") { i += 1; continue; }
    if (!["-simplematch", "-casesensitive", "-notmatch", "-allmatches", "-raw"].includes(flag)) return false;
  }
  return true;
}

/** The line window a step's filter chain leaves open, or null when any stage
 * could reorder, transform or redirect what flows through it. */
function stepWindow({ producer, filters }) {
  if (producer.redirects.length || filters.some((f) => f.redirects.length)) return null;
  let start = 1;
  let len = Infinity;
  let positioned = true;
  let numbered = false;
  for (const f of filters) {
    if (f.program === "sed") {
      const range = sedFilterRange(f);
      if (!range) return null;
      if (Number.isFinite(len) && range.from > len) return null;
      const remaining = Number.isFinite(len) ? len - (range.from - 1) : Infinity;
      start += range.from - 1;
      len = Math.min(remaining, range.to - range.from + 1);
    } else if (f.program === "head") {
      const count = headFilterCount(f);
      if (count == null) return null;
      len = Math.min(len, count);
    } else if (f.program === "tail") {
      // The lines are real; their absolute positions are not.
      positioned = false;
      len = Infinity;
    } else if (f.program === "grep" || f.program === "rg") {
      const values = f.args.filter((a) => a.kind === "value");
      if (values.length !== 1 || !f.args.every((a) => a.kind === "value" || GREP_FILTER_FLAGS.test(a.text))) return null;
      if (f.args.some((a) => /^-[a-z]*n[a-z]*$/.test(a.text))) {
        // -n numbers the FILTER's input stream. That equals file numbering
        // only while the stream still starts at the file's first line; after
        // a range shift or a tail the numbers are stream-relative, and the
        // gutter they add pollutes the content besides - so anything but the
        // from-line-1 case refuses the view outright.
        if (!positioned || start !== 1) return null;
        numbered = true;
      }
      positioned = false;
      len = Infinity;
    } else if (f.program === "cat" && !f.args.some((a) => a.kind === "value")) {
      continue;
    } else if (f.program === "select-object") {
      const sel = selectObjectWindow(f);
      if (!sel) return null;
      if (sel.last != null) {
        // The last K lines: real, at positions the stream does not state.
        positioned = false;
        len = Math.min(len, sel.last);
        continue;
      }
      if (Number.isFinite(len) && sel.skip >= len) return null;
      const remaining = Number.isFinite(len) ? len - sel.skip : Infinity;
      start += sel.skip;
      len = Math.min(remaining, sel.take ?? Infinity);
    } else if (f.program === "select-string") {
      if (!selectStringFilters(f)) return null;
      positioned = false;
      len = Infinity;
    } else if (PS_FOREACH_PROGRAMS.has(f.program) && producer.program === "select-string" && filters.length === 1
      && selectStringTemplate(f)) {
      // A search printing through its template: the lines carry the file's
      // numbers themselves (templateObservation), so the window states none.
      positioned = false;
      len = Infinity;
    } else return null;
  }
  return { relStart: positioned ? start : null, relLen: Number.isFinite(len) ? len : null, numbered };
}

/** The call's commands that print into its result, in source order. */
function topCommands(reading) {
  const sorted = [...reading.commands].sort((a, b) => a.start - b.start);
  const top = [];
  for (const c of sorted) {
    const prev = top[top.length - 1];
    // A command inside another's span is a substitution: its output became an
    // argument, never a line of the result.
    if (prev && c.start >= prev.start && c.end <= prev.end) continue;
    top.push(c);
  }
  return top;
}

/** Group the call's top-level commands into pipelines, in source order. */
function stepsOf(reading, source) {
  const top = topCommands(reading);
  const steps = [];
  let current = null;
  for (const c of top) {
    if (!current) { current = { producer: c, filters: [] }; continue; }
    const previous = current.filters[current.filters.length - 1] ?? current.producer;
    const between = source.slice(previous.end, c.start);
    if (PIPE_CONNECTOR.test(between)) { current.filters.push(c); continue; }
    if (!STEP_CONNECTOR.test(between)) return null;
    steps.push(current);
    current = { producer: c, filters: [] };
  }
  if (current) steps.push(current);
  return steps;
}

/**
 * PowerShell's statements as the steps of one output stream. Its markers and
 * its quiet statements are not commands - a bare string prints itself, an
 * assignment prints nothing - so the steps come from the grammar's statement
 * list rather than from the source between commands. A statement this cannot
 * follow (a loop, a condition, an expanding string) may print anything, so it
 * is a step that owns its span and places nothing.
 */
function powershellSteps(reading) {
  const top = topCommands(reading);
  const steps = [];
  for (const st of reading.statements || []) {
    if (st.kind === "empty") continue;
    if (st.kind === "assignment") { steps.push({ kind: "silent" }); continue; }
    if (st.kind === "string") {
      steps.push(st.value != null ? { kind: "marker", marker: st.value } : { kind: "blind" });
      continue;
    }
    const commands = st.kind === "commands" ? top.filter((c) => c.start >= st.start && c.end <= st.end) : [];
    if (!commands.length) { steps.push({ kind: "blind" }); continue; }
    const [producer, ...filters] = commands;
    if (!filters.length && PS_SILENT_PROGRAMS.has(producer.program)) { steps.push({ kind: "silent" }); continue; }
    if (!filters.length && PS_MARKER_PROGRAMS.has(producer.program) && producer.args.length === 1) {
      const a = producer.args[0];
      const marker = powershellLiteral(a.text) ?? (a.resolved && a.kind === "value" && !/^["']/.test(a.text) ? a.value : null);
      steps.push(marker != null ? { kind: "marker", marker } : { kind: "blind" });
      continue;
    }
    steps.push({ kind: "step", producer, filters });
  }
  return steps;
}

// The template a PowerShell search prints its matches through: its statement
// is the search piped into one templated ForEach-Object.
function pipedTemplate(reading, command) {
  const top = topCommands(reading);
  for (const st of reading.statements || []) {
    if (st.kind !== "commands" || command.start < st.start || command.end > st.end) continue;
    const commands = top.filter((c) => c.start >= st.start && c.end <= st.end);
    return commands.length === 2 && commands[0] === command && PS_FOREACH_PROGRAMS.has(commands[1].program)
      ? selectStringTemplate(commands[1])
      : null;
  }
  return null;
}

// Where the statement holding `command` falls among the PowerShell statements
// that print: the first of them begins the stream and the last ends it.
// `{ first, last, commands }`, or null.
function printingEdges(reading, command) {
  if (reading.language !== "powershell" || reading.nested.length) return null;
  const top = topCommands(reading);
  const printing = [];
  let at = -1;
  for (const st of reading.statements || []) {
    if (st.kind === "empty" || st.kind === "assignment") continue;
    const commands = st.kind === "commands" ? top.filter((c) => c.start >= st.start && c.end <= st.end) : [];
    if (commands.length === 1 && PS_SILENT_PROGRAMS.has(commands[0].program)) continue;
    if (commands.includes(command)) at = printing.length;
    printing.push(commands);
  }
  if (at < 0 || printing[at].some((c) => c.redirects.length)) return null;
  return { first: at === 0, last: at === printing.length - 1, commands: printing[at] };
}

// Whether `command` stands alone in the last PowerShell statement that prints
// anything, so that what it printed ends the stream.
function lastToPrint(reading, command) {
  const edges = printingEdges(reading, command);
  return Boolean(edges?.last) && edges.commands.length === 1;
}

/**
 * Whether a line holds a match of the search's own `-Pattern`: true of every
 * line Select-String printed, by construction. Null when there is no stated
 * pattern to test, when -NotMatch inverts it, or when the pattern reads
 * differently here than in .NET - its anchors (`\A`, `\Z`, `\G`), Unicode
 * classes and class subtraction - or does not compile here at all.
 */
function selectStringMatcher(command) {
  const flags = command.args.filter((a) => a.kind === "flag").map((a) => a.text.toLowerCase());
  if (flags.includes("-notmatch")) return null;
  const at = command.args.findIndex((a) => a.kind === "flag" && /^-Pattern$/i.test(a.text));
  if (at < 0) return null;
  const patterns = [];
  for (let i = at + 1; i < command.args.length; i += 1) {
    const a = command.args[i];
    // `-Pattern a,b` arrives as `a` and `,b`: the comma continues the list.
    if (a.kind === "flag" || (i > at + 1 && !a.text.startsWith(","))) break;
    if (!a.resolved) return null;
    patterns.push(a.value);
  }
  if (!patterns.length) return null;
  const cased = flags.includes("-casesensitive");
  if (flags.includes("-simplematch")) {
    const needles = patterns.map((p) => (cased ? p : p.toLowerCase()));
    return (line) => needles.some((n) => (cased ? line : line.toLowerCase()).includes(n));
  }
  if (patterns.some((p) => /\\[AZzGpP]|-\[/.test(p))) return null;
  try {
    const res = patterns.map((p) => new RegExp(p, cased ? "" : "i"));
    return (line) => res.some((re) => re.test(line));
  } catch {
    return null;
  }
}

/**
 * A templated search sharing its stream with statements before or after it:
 * its lines are the run at the stream's head when it printed first, or at its
 * tail when it printed last - strings, so none was cut. Nothing marks where
 * the run ends, so each line must fit the template, follow file order and
 * hold a match of the search's own pattern; that is what keeps a neighbour's
 * numbered line out.
 */
function edgeTemplateObservation(text, template, matches, edges) {
  const lines = String(text || "").split("\n");
  while (lines.length && !lines[lines.length - 1].trim()) lines.pop();
  while (lines.length && !lines[0].trim()) lines.shift();
  const fits = (line) => {
    const m = template.pattern.exec(line);
    return Boolean(m) && matches(m[2]);
  };
  let run = [];
  if (edges.last) {
    let start = lines.length;
    while (start > 0 && fits(lines[start - 1])) start -= 1;
    run = lines.slice(start);
  } else if (edges.first) {
    let end = 0;
    while (end < lines.length && fits(lines[end])) end += 1;
    run = lines.slice(0, end);
  }
  return run.length ? templateObservation(run.join("\n"), template) : null;
}

// A bash pipeline made a step: an echo of literal words is a marker, a quiet
// builtin prints nothing, anything else produces.
function bashStep({ producer, filters }) {
  if (!filters.length && producer.program === "echo" && producer.args.length
    && producer.args.every((a) => a.resolved && a.kind === "value")) {
    return { kind: "marker", marker: producer.args.map((a) => a.value).join(" ") };
  }
  if (!filters.length && SILENT_PROGRAMS.has(producer.program)) return { kind: "silent" };
  return { kind: "step", producer, filters };
}

/**
 * Split a span by the producers' own numbering: each numbering producer
 * prints its file's line numbers from where its range starts, one apart, so
 * its run of the stream is exactly the lines that carry them - whatever the
 * file's length, which is what arithmetic cannot know. A producer without
 * numbering ends where nothing can tell, so nothing after it places; and a
 * run the next numbered read could continue has no knowable end, so the split
 * stops there too.
 */
function gutterSplit(span, producers, views) {
  let at = 0;
  for (let i = 0; i < producers.length; i += 1) {
    const { producer, window, cap, gutterStart } = producers[i];
    if (gutterStart == null) return;
    const nextStart = producers[i + 1]?.gutterStart ?? null;
    const from = at;
    let expect = gutterStart;
    while (at < span.length && at - from < cap) {
      const m = GUTTER_LINE.exec(span[at]);
      if (!m || Number(m[1]) !== expect) break;
      if (at > from && expect === nextStart) return;
      at += 1;
      expect += 1;
    }
    if (at === from) {
      // Nothing numbered where this run should start. The read printed
      // nothing only if what follows is the next run's first line, or the end.
      const next = span[at] == null ? null : GUTTER_LINE.exec(span[at]);
      if (span[at] != null && !(nextStart != null && next && Number(next[1]) === nextStart)) return;
      views.set(producer.start, { text: "", relStart: window.relStart, numbered: false, empty: true });
      continue;
    }
    views.set(producer.start, { text: `${span.slice(from, at).join("\n")}\n`, relStart: window.relStart, numbered: false });
  }
}

/**
 * Which slice of the result belongs to which command, keyed by the producing
 * command's start index. Empty whenever the structure gives no sound answer.
 */
function segmentViews(reading, source, resultText, failed) {
  if (failed || !resultText || reading.nested.length) return new Map();
  const steps = reading.language === "powershell"
    ? powershellSteps(reading)
    : reading.language === "bash" ? stepsOf(reading, String(source ?? ""))?.map(bashStep) : null;
  if (!steps) return new Map();

  const entries = steps.map((step) => {
    if (step.kind === "marker" || step.kind === "silent") return step;
    // A step nothing can follow still owns the lines it printed, so its span
    // places nothing rather than lending them to a neighbour.
    if (step.kind === "blind") return { kind: "producer", producer: null, window: null, cap: Infinity, gutterStart: null };
    const { producer } = step;
    const window = stepWindow(step);
    // A filter chain and the producer itself each bound the step; the tighter
    // one holds. No window means the step's output is not this stream at all.
    const cap = window ? Math.min(window.relLen ?? Infinity, producerCap(producer)) : Infinity;
    // Where a numbering producer's run of the stream starts: its gutter
    // carries the file's own line numbers, from the first line its filter
    // lets through.
    const gutterStart = window?.relStart != null && !window.numbered && numbersEveryLine(producer) ? window.relStart : null;
    return { kind: "producer", producer, window, cap, gutterStart };
  });
  // No markers means one span holding every producer, which only a split can
  // divide: arithmetic needs a bound on each, numbering needs the first to
  // carry a gutter.
  const producers = entries.filter((e) => e.kind === "producer");
  if (!entries.some((e) => e.kind === "marker") && producers.length > 1
    && !producers.every((e) => Number.isFinite(e.cap)) && producers[0].gutterStart == null) return new Map();

  const lines = String(resultText).split("\n");
  if (lines.length && lines[lines.length - 1] === "") lines.pop();
  // PowerShell's formatter closes a group of objects - Select-String's
  // matches, a table - with a blank line it may write only at the very end of
  // the stream, inside the last statement's span, and each group it closes
  // adds one. Trailing blank lines there cannot be told from those, so none is
  // any statement's.
  if (reading.language === "powershell") while (lines.length && !lines[lines.length - 1].trim()) lines.pop();

  const views = new Map();
  let spanStart = 0;
  let spanProducers = [];
  const flush = (endExclusive) => {
    const count = endExclusive - spanStart;
    if (spanProducers.length === 1) {
      const { producer, window, cap } = spanProducers[0];
      // An error the read printed is not a line of the file, here any more
      // than in a split.
      const clean = !lines.slice(spanStart, endExclusive).some((line) => READ_ERROR_LINE.test(line));
      if (window && clean && count > 0 && count <= cap) {
        views.set(producer.start, {
          text: `${lines.slice(spanStart, endExclusive).join("\n")}\n`,
          relStart: window.relStart,
          numbered: window.numbered,
        });
      } else if (window && count === 0) {
        // A zero-line span between two matched markers is not a refusal: the
        // command ran and produced NOTHING, which for a search is a finding
        // (the file holds no match) and deserves different words than
        // "nothing was attributable".
        views.set(producer.start, { text: "", relStart: window.relStart, numbered: window.numbered, empty: true });
      }
    } else if (spanProducers.length > 1) {
      const total = spanProducers.reduce((sum, p) => sum + p.cap, 0);
      const span = lines.slice(spanStart, endExclusive);
      if (total === count && !span.some((line) => READ_ERROR_LINE.test(line))) {
        let at = 0;
        for (const { producer, window, cap } of spanProducers) {
          views.set(producer.start, cap
            ? { text: `${span.slice(at, at + cap).join("\n")}\n`, relStart: window.relStart, numbered: window.numbered }
            : { text: "", relStart: window.relStart, numbered: window.numbered, empty: true });
          at += cap;
        }
      } else {
        gutterSplit(span, spanProducers, views);
      }
    }
    spanProducers = [];
  };
  for (const entry of entries) {
    if (entry.kind === "silent") continue;
    if (entry.kind === "producer") { spanProducers.push(entry); continue; }
    let at = -1;
    for (let i = spanStart; i < lines.length; i += 1) {
      if (lines[i] === entry.marker) { at = i; break; }
    }
    // A marker the output does not carry means the structure did not survive
    // the run. Segments already closed on both sides stay; nothing after the
    // break is trustworthy.
    if (at < 0) return views;
    flush(at);
    spanStart = at + 1;
  }
  flush(lines.length);
  return views;
}

/**
 * Git, split between the file it named and the tree it acted on.
 *
 * `gh`, `tea` and `fj` are deliberately absent: traceForgeOps owns the forge
 * channels, and a second claim here would put two ops on one call.
 */
function emitGit(command, emit, { resultText, bare, failed, seg = null }) {
  // Peel the prefix flags before reading the verb, and keep `-C`'s directory:
  // it is the repository this invocation acted on, which is the lane.
  let repo = null;
  const rest = [];
  for (let i = 0; i < command.args.length; i += 1) {
    const a = command.args[i];
    if (GIT_PREFIX_FLAGS.test(a.text)) {
      const value = command.args[i + 1];
      if (/^-C$/.test(a.text) && value?.resolved) {
        repo = value.value.replace(/[\\/]+$/, "").split(/[\\/]/).pop() || value.value;
      }
      i += 1;
      continue;
    }
    rest.push(a);
  }
  const verb = rest.find((a) => a.kind === "value" && !/^-/.test(a.text))?.value?.toLowerCase();
  if (!verb) return false;

  // Everything after a bare `--` is a pathspec: files this invocation was
  // scoped to.
  const dashdash = rest.findIndex((a) => a.text === "--");
  const pathspec = dashdash >= 0
    ? rest.slice(dashdash + 1).filter((a) => a.resolved && looksLikeFile(a.value))
    : [];

  // `git show <rev>:<path>` prints the file at that revision: a whole file from
  // line 1, positioned exactly like `cat`. Two rev:paths concatenate into one
  // stream, exactly like `cat a b`: both reads are real, the result describes
  // neither alone, so the gate withholds content unless the target is singular.
  let showedFile = false;
  if (verb === "show") {
    const shown = [];
    for (const a of rest) {
      if (!a.resolved) continue;
      const m = GIT_REV_PATH.exec(a.value);
      if (m && looksLikeFile(m[2])) shown.push(m);
    }
    for (const m of shown.slice(0, MAX_PATHS_PER_COMMAND)) {
      // A segment view carries attribution the bare gate could not: this
      // command's own slice of the result, with any range filter's offset.
      const view = seg
        ? observe({ resultText: seg.text, bare: true, failed, targets: shown.length, start: seg.relStart, whole: false, numbered: seg.numbered })
        : observe({ resultText, bare, failed, targets: shown.length, start: 1, whole: true });
      emit("files", {
        kind: "read",
        path: m[2],
        plus: 0,
        minus: 0,
        readLines: view.readLines,
        obs: view.obs,
        obsFull: view.obsFull,
        rev: m[1],
        basis: `git show ${m[1]}`,
        ok: !failed,
      });
      showedFile = true;
    }
  }

  // A pathspec names the file. Under a tree-mutating verb that naming IS a
  // write to it; under log/diff the output is history, not contents.
  // `rm --cached` unstages without touching the file, so it stays a read.
  const mutates = GIT_PATHSPEC_WRITE_VERBS.has(verb)
    && !(verb === "rm" && rest.some((a) => a.text === "--cached"));
  // git grep is the searcher wearing git's clothes: with -n its output is
  // self-positioned file content, exactly like the shell spelling - and
  // refusing it content here while shell grep carried some made one reading
  // differ by which door it came through. One pathspec only: the numbers
  // arrive with a per-file prefix, and two files interleave in one stream.
  // The same trust rule as the searcher branch applies to a segment view:
  // numbering a FILTER added is stream-relative and refuses.
  const grepNumbered = verb === "grep" && rest.some((a) => /^-[a-z]*n[a-z]*$/.test(a.text));
  const grepView = !mutates && grepNumbered
    ? (seg
      ? observe({ resultText: seg.numbered ? "" : seg.text, bare: true, failed, targets: pathspec.length, numbered: true })
      : observe({ resultText, bare, failed, targets: pathspec.length, numbered: true }))
    : null;
  for (const a of pathspec.slice(0, MAX_PATHS_PER_COMMAND)) {
    emit("files", mutates
      ? { kind: "write", path: a.value, plus: 0, minus: 0, basis: `git ${verb}`, ok: !failed }
      : {
        kind: "read",
        path: a.value,
        plus: 0,
        minus: 0,
        readLines: grepView?.readLines ?? 0,
        obs: grepView?.obs ?? [],
        obsFull: false,
        observedEmpty: grepNumbered && seg?.empty === true,
        basis: `git ${verb}`,
        ok: !failed,
      });
  }

  const isWrite = GIT_WRITE_VERBS.has(verb);
  if (!isWrite && !GIT_READ_VERBS.has(verb)) {
    // An unknown verb is unread work, not a silent no-op - unless the call
    // already placed the file it named.
    return showedFile || pathspec.length > 0;
  }
  emit("vcs", {
    // The lane's marks read as work on the tree: a commit changes it, a log
    // does not.
    kind: isWrite ? "write" : "read",
    repo,
    verb,
    scoped: pathspec.map((a) => a.value).slice(0, 8),
    basis: `git ${verb}`,
    ok: !failed,
  });
  return true;
}

/** A build tool's run, on the tool's own lane, carrying its outcome. */
function emitBuild(command, emit, { failed }) {
  const spec = BUILD_TOOLS[command.program];
  const args = command.args.filter((a) => a.kind === "value" && a.resolved
    && !(spec.skip && spec.skip.test(a.text)));
  const verb = args[0]?.value ?? null;
  emit("build", {
    kind: "run",
    tool: command.program,
    verb,
    // `npm run lint` and `cargo +nightly clippy` both read better as the pair.
    label: verb ? `${command.program} ${verb}` : command.program,
    basis: `shell ${command.program}`,
    ok: !failed,
  });
  return true;
}

/**
 * One shell call's work, as lane items.
 *
 * `bare` is what makes content attributable: a single command, unpiped and
 * unredirected, whose tool result IS its output. Anything else still reports
 * the subjects it consulted, without claiming the result describes them.
 */
function workOfCall(call, reading, paired, alsoClaimed = false, capability = null) {
  const powershell = reading.language === "powershell";
  // PowerShell's result arrives with Windows line endings; the `\r` is the
  // transport's, not a character of any line the command printed.
  const resultText = paired?.text == null ? null : powershell ? paired.text.replace(/\r\n/g, "\n") : paired.text;
  const failed = Boolean(paired?.failed);
  const items = [];
  const unclaimed = [];
  // A PowerShell statement that is not a command can still print - a bare
  // string prints itself - so one command is the whole output only when every
  // other statement is quiet.
  const bare = reading.commands.length === 1
    && !reading.commands[0].redirects.length
    && !reading.nested.length
    && (!powershell || (reading.statements || []).every((st) => st.kind === "empty" || st.kind === "assignment"
      || (st.kind === "commands" && st.start <= reading.commands[0].start && reading.commands[0].end <= st.end)));
  // When the call is not bare, the segmentation pass may still hand individual
  // commands their own slice of the result; a bare call needs no help.
  const views = bare ? new Map() : segmentViews(reading, call.command, resultText, failed);

  // Where a relative subject is rooted, per the same-line cwd fold above.
  // "" is the call's own root; null is poisoned - a cd made the root
  // unknowable, and relative subjects after it have no lane-joinable spelling,
  // so they go to the census instead of a wrong lane.
  let cwd = "";
  const subjectPath = (value) => {
    if (cwd === "") return value;
    if (isRooted(value)) return value;
    return cwd == null ? null : joinSubject(cwd, value);
  };

  const emit = (domain, op) => {
    items.push({
      id: `shellwork:${call.key}#${items.length}`,
      domain,
      // The lane this op belongs to, named here so a caller can count how many
      // distinct subjects one turn touched without rebuilding the model.
      subject: `${domain}:${op.path || op.repo || op.tool || op.url || domain}`,
      // Whether a committed extractor also read this call. Not a conflict: a
      // shared call is one action seen from two sides.
      shared: alsoClaimed,
      actionId: call.actionId,
      op: {
        ...op,
        // How far the command this op came from could reach. An attribute of
        // the work, so it travels with it. Deliberately NOT the pass's own
        // verdict about whether it could clear the call: that is a fact about
        // the reader, and a reader looking at a lane wants the reach.
        reach: capability?.reach ?? null,
        key: `${call.key}#${items.length}`,
        // Every op says how it was obtained. A reader that cannot tell a
        // grammar-derived read from a tool-reported one cannot weigh them.
        derived: true,
        basis: op.basis || "shell grammar",
        ok: op.ok !== false,
      },
      turnIndex: call.turnIndex,
      blockIndex: call.blockIndex,
    });
  };

  for (const command of [...reading.commands, ...reading.nested]) {
    if (!command.program || command.opaque) continue;
    if (CWD_PROGRAMS.has(command.program)) {
      cwd = foldCwd(cwd, command);
      continue;
    }
    if (command.program === "popd" || command.program === "pop-location") {
      // popd restores a directory this fold did not keep.
      cwd = null;
      continue;
    }
    const reader = READERS[command.program];
    const searcher = SEARCHERS[command.program];
    const fetcher = FETCHERS.has(command.program);

    if (reader) {
      const start = reader.start(command);
      let candidates = subjectArgs(command, reader);
      if (reader.scriptFlags && !command.args.some((a) => reader.scriptFlags.test(a.text))) {
        candidates = candidates.slice(1);
      }
      const paths = candidates
        .filter((a) => a.resolved && looksLikePath(a.value))
        .slice(0, MAX_PATHS_PER_COMMAND);
      // The searcher's guard, for the same reason it has one: `head -20
      // src/components/` consulted a tree, and a files lane for a directory
      // claims a file that does not exist.
      const files = paths.filter((a) => looksLikeFile(a.value));
      const seg = views.get(command.start);
      let placed = 0;
      for (const f of files) {
        const path = subjectPath(f.value);
        if (path == null) continue;
        const guttered = Boolean(reader.guttered?.(command));
        // The segment's window is relative to this command's own output, so
        // it composes onto the position the command itself states. Numbering
        // inherited from a filter is trusted only when this command's output
        // began at the file's line 1: a range-stating producer (`sed -n
        // '570,585p' f | grep -n x`) feeds the filter a stream whose numbers
        // are stream-relative, and the gutter pollutes the content besides,
        // so that case refuses rather than states wrong positions.
        const view = seg && !guttered
          ? observe({
            resultText: seg.numbered && start !== 1 ? "" : seg.text,
            bare: true,
            failed,
            targets: paths.length,
            start: start == null || seg.relStart == null ? null : start + seg.relStart - 1,
            whole: false,
            numbered: seg.numbered,
          })
          // A gutter that numbers every line positions its own slice, filtered
          // or not: the numbers ARE the file's.
          : seg && numbersEveryLine(command)
            ? observe({ resultText: seg.text, bare: true, failed, targets: paths.length, guttered: true })
            : observe({
            resultText,
            bare,
            failed,
            targets: paths.length,
            start,
            whole: typeof reader.whole === "function" ? reader.whole(command) : Boolean(reader.whole),
            guttered,
          });
        emit("files", {
          kind: "read",
          path,
          plus: 0,
          minus: 0,
          readLines: view.readLines,
          obs: view.obs,
          obsFull: view.obsFull,
          observedEmpty: seg?.empty === true,
          basis: `shell ${command.program}`,
          ok: !failed,
        });
        placed += 1;
      }
      // A reader with no path at all filtered a stream (`... | head -20`):
      // plumbing. One whose every path refused a lane - a directory, or a
      // relative path under a poisoned cwd - is real work with nowhere to
      // land, so it is counted rather than invented.
      if (!placed && paths.length) unclaimed.push(command.program);
      continue;
    }

    if (searcher) {
      const seg = views.get(command.start);
      const numbered = command.args.some((a) => searcher.numbered.test(a.text));
      const args = subjectArgs(command, searcher).filter((a) => a.resolved);
      // The first positional is the pattern - unless a flag already carried
      // it, in which case every remaining subject is a place it looked.
      const patternCarried = Boolean(searcher.patternFlags)
        && command.args.some((a) => searcher.patternFlags.test(a.text));
      const targets = (patternCarried ? args : args.slice(1))
        .filter((a) => looksLikePath(a.value)).slice(0, MAX_PATHS_PER_COMMAND);
      const files = targets.filter((a) => looksLikeFile(a.value));
      let placed = 0;
      for (const f of files) {
        const path = subjectPath(f.value);
        if (path == null) continue;
        // A searcher's output is a non-contiguous subset of the file, so
        // numbering inherited from a downstream filter is stream-relative and
        // never trustworthy here; only the searcher's own -n - whose numbers
        // ride inside the content through any subsetting filter - positions.
        // Several files searched with numbering: each line names its file.
        let view;
        if (command.program === "select-string") {
          // Select-String reading files numbers every line it prints, path and
          // all, and gets a parse of its own (selectStringObservation) - or
          // prints through a template that numbers them (templateObservation).
          // Without a span of its own, a search that printed last still owns
          // the tail of the stream (trailingSelectStringRecords).
          const others = files.filter((x) => x.value !== f.value).map((x) => x.value);
          const template = pipedTemplate(reading, command);
          let obs = null;
          if (!failed && template && files.length === 1) {
            if (seg) obs = templateObservation(seg.text, template);
            else {
              const edges = printingEdges(reading, command);
              const matches = edges && (edges.first || edges.last) ? selectStringMatcher(command) : null;
              if (matches) obs = edgeTemplateObservation(resultText, template, matches, edges);
            }
          } else if (!failed && !template) {
            if (seg || bare) obs = selectStringObservation(seg ? seg.text : resultText, f.value, others);
            if (!obs && !seg && lastToPrint(reading, command)) obs = trailingSelectStringRecords(resultText, f.value, others);
          }
          view = { obs: obs || [], readLines: obs?.length || 0, obsFull: false, obsTrimmed: Boolean(obs && template?.trimmed) };
        } else {
          const prefixed = numbered && files.length > 1 && !failed ? prefixedObservation(seg ? seg.text : resultText, f.value) : null;
          view = prefixed
            ? { obs: prefixed, readLines: prefixed.length, obsFull: false }
            : seg
              ? observe({
                resultText: seg.numbered && !numbered ? "" : seg.text,
                bare: true,
                failed,
                targets: files.length,
                numbered,
              })
              : observe({ resultText, bare, failed, targets: files.length, numbered });
        }
        emit("files", {
          kind: "read",
          path,
          plus: 0,
          minus: 0,
          readLines: view.readLines,
          obs: view.obs,
          obsFull: false,
          // The texts are the lines less their surrounding whitespace.
          ...(view.obsTrimmed ? { obsTrimmed: true } : {}),
          observedEmpty: seg?.empty === true,
          basis: `shell ${command.program}`,
          ok: !failed,
        });
        placed += 1;
      }
      // A search over a DIRECTORY consulted a tree - real work with no lane to
      // land in, so it is counted rather than invented. A search with no path
      // at all filtered a stream, which is plumbing.
      if (!placed && targets.length) unclaimed.push(command.program);
      continue;
    }

    if (command.program === "git") {
      // A verb neither table knows is unread work, not a silent no-op: the
      // handler says whether it placed anything and the census believes it.
      if (!emitGit(command, emit, { resultText, bare, failed, seg: views.get(command.start) ?? null }) && !alsoClaimed) {
        unclaimed.push(command.program);
      }
      continue;
    }

    if (BUILD_TOOLS[command.program]) {
      emitBuild(command, emit, { failed });
      continue;
    }

    if (fetcher) {
      const urls = command.args.filter((a) => a.resolved && looksLikeUrl(a.value)).slice(0, MAX_PATHS_PER_COMMAND);
      for (const u of urls) {
        emit("research", { kind: "fetch", url: u.value, basis: `shell ${command.program}`, ok: !failed });
      }
      if (!urls.length) unclaimed.push(command.program);
      continue;
    }

    // An interpreter wrapper whose payload was followed is not unread work:
    // its commands are flattened into this same walk and placed or censused
    // under their own names. Counting `bash -c "cat f"` as an unread `bash`
    // reported the door's own mechanism as its backlog.
    const payload = interpreterPayload(command);
    if (payload && SHELL_LANGUAGES.includes(payload.language)) continue;

    // Work this module has no rule for. A call a committed extractor ALREADY
    // read is not a backlog item even though nothing was emitted here: that
    // work is legible, just not by this door. Counting it would have reported
    // `tofu` and `kubectl` as unread while the installed IaC lens was reading
    // them all along.
    if (!PLUMBING.has(command.program) && !alsoClaimed) unclaimed.push(command.program);
  }
  return { items, unclaimed };
}


// One semantic feed per turns array, remembered by REFERENCE. The feed is a
// full pass over the conversation and this module needs only one thing from it,
// so a re-render that hands back the same turns must not pay for it twice - and
// `buildTraceLanes` is already building its own.
let claimedCacheKey = null;
let claimedCacheValue = null;

function claimedKeysOf(turns) {
  const list = Array.isArray(turns) ? turns : [];
  if (claimedCacheKey === list) return claimedCacheValue;
  claimedCacheKey = list;
  claimedCacheValue = buildSemanticFeed(list).claimedKeys || new Set();
  return claimedCacheValue;
}

/**
 * Read every shell call in a trace for the work it did.
 *
 * @returns {{items, census, counts}}
 *   `items` go to `buildTraceLanes` as `extraItems`.
 *   `census` ranks the programs whose work found no lane - which is the
 *   backlog for the next rule, observed rather than guessed. It is reported
 *   rather than dropped because the point of this module is to say how much of
 *   an organization's shell work is legible, and a silent residual would let
 *   the answer look like "all of it".
 */
export async function readShellWork(turns, { signal = null, onProgress = null, actions = null } = {}) {
  const { calls, dropped } = actionShellCalls(actions || buildTraceActions(turns));
  // Which calls a committed extractor already read. This is NOT a reason to
  // stand down. One call routinely does work in several domains at once, and
  // the lanes plane is subjects by turns precisely so that shows: `git push`
  // is a forge action AND a change to the local repository, `cat a.txt >
  // b.txt` reads one file and writes another. Suppressing the second reading
  // was tried and it cost 5.8 points of placement over a real corpus while
  // hiding exactly the calls whose reach is widest.
  //
  // So the overlap is RECORDED rather than resolved: an op says whether the
  // call it came from already had a committed claim, which is what lets a
  // reader see that a turn landed on several subjects instead of guessing why
  // two rows lit up at once.
  const claimed = claimedKeysOf(turns);
  const items = [];
  const census = new Map();
  const counts = { calls: calls.length, claimed: 0, unclaimed: 0, shared: 0, dropped: dropped ? 1 : 0 };

  // Publishing and yielding are different obligations and must not share a
  // stride. Every publish costs the consumer a full O(trace) lane-model
  // rebuild, so the publish COUNT decides whether a fresh trace load is
  // linear or quadratic: a fixed call stride scaled with the trace (80
  // rebuilds at the 2,000-call cap - 4.2s of churn against 0.3s of actual
  // reading), while a fixed fraction of the call list caps it at
  // MAX_PUBLISHES whatever the size. The yield keeps the small fixed stride
  // because its job is unchanged: return a macrotask to the frame loop often
  // enough that the page can paint.
  const publishStride = Math.max(YIELD_STRIDE, Math.ceil(calls.length / MAX_PUBLISHES));

  for (let i = 0; i < calls.length; i += 1) {
    if (signal?.aborted) return { items, census, counts, partial: true };
    const call = calls[i];
    // The feed claims a call by its action's id, whichever block it read: a
    // copy's claim lands on the cell it came from.
    const alsoClaimed = claimed.has(call.actionId);
    const reading = await readShellCommands(call.command, { language: call.language });
    if (!reading) continue;
    const paired = { text: call.result.text, failed: !call.result.ok, paired: call.result.paired };
    // What the call could do at worst, carried ON the ops rather than reported
    // separately. A strip counting how many calls a pass cleared describes the
    // pass; a reader wants to know what the command in front of them could
    // reach, at the moment they are looking at it. The parse is shared - the
    // tree is already cached by content - so this is a walk, not a second read.
    const capability = await readCapability(call.command, { language: call.language });
    const { items: found, unclaimed } = workOfCall(call, reading, paired, alsoClaimed, capability);
    if (found.length) {
      counts.claimed += 1;
      // A call another extractor also read is SHARED, not resolved: both
      // readings stand, and the count is how often that happened.
      if (alsoClaimed) counts.shared += 1;
    } else {
      counts.unclaimed += 1;
    }
    items.push(...found);
    for (const program of unclaimed) {
      const row = census.get(program) || { program, calls: 0 };
      row.calls += 1;
      census.set(program, row);
    }
    // The last call publishes nothing: the final result follows immediately.
    if (i % publishStride === publishStride - 1 && i !== calls.length - 1) {
      onProgress?.({ items: [...items], counts: { ...counts }, partial: true });
    }
    if (i % YIELD_STRIDE === YIELD_STRIDE - 1) {
      // A MACROTASK, not just the await above. Parsing is synchronous once a
      // grammar is loaded and each call may spend up to the parse deadline, so
      // a long trace holds the main thread for as long as it takes unless the
      // loop actually returns to the event loop. `await` on an already-resolved
      // promise is a microtask and never lets a frame paint - which made the
      // progress callback above report into a thread that could not draw.
      await new Promise((resolve) => { setTimeout(resolve, 0); });
    }
  }
  return { items, census, counts, partial: false };
}

// -- measurement ------------------------------------------------------------
// Not rendered anywhere. How much of an organization's shell work this door can
// read is a question about the door, and the answer belongs to whoever is
// deciding which rule to write next - not on a strip above a reader's trace.

/** The unclaimed programs, heaviest first: the rule backlog, from the corpus. */
export function shellWorkCensus(result, limit = 12) {
  return [...(result?.census?.values() || [])]
    .sort((a, b) => b.calls - a.calls)
    .slice(0, limit);
}

/** One line on how much of this trace's shell work found a lane. */
export function shellWorkSummaryLine(result) {
  const c = result?.counts;
  if (!c?.calls) return "no shell calls";
  const share = Math.round((c.claimed / c.calls) * 100);
  const top = shellWorkCensus(result, 1)[0];
  return `${c.claimed} of ${c.calls} shell calls placed on a subject lane (${share}%)`
    + (top ? `; ${top.program} is the largest unread` : "");
}

export { detectShellLanguage, shellToolCommand };
